<script>
import responsiveMixin from '@shared/mixins/responsive';
import Offline from '@shared/components/Offline.vue';
import godfatherMixin from '@app/mixins/godfather';
import Page404 from '@app/pages/404.vue';
import AskOwnerPhone from '@app/components/AskOwnerPhone.vue';
import ResponsiveNoAccessMessage from '@app/components/ResponsiveNoAccessMessage.vue';
import OnboardingQuestions from '@app/components/OnboardingQuestions.vue';
import AnnouncementModal from '@app/components/AnnouncementModal.vue';
import { getCurrentDate } from '@shared/utils/timeTravel';

export default {
  name: 'App',
  mixins: [godfatherMixin, responsiveMixin({ isMobileScreen: 768 })],
  components: {
    Offline,
    Page404,
    AskOwnerPhone,
    ResponsiveNoAccessMessage,
    OnboardingQuestions,
  },
  head: {
    title: 'Teachizy+',
    titleTemplate: '%s | Teachizy+',
  },
  data() {
    return {

      announcementStartDate: new Date(2025, 6, 20), // July 20, 2025
      announcementEndDate: new Date(2025, 7, 2, 23, 59, 59), // August 02, 2025 end of day
      // Garde-fou : la popup d'annonce ne doit s'ouvrir qu'une fois par session,
      // même si `authUser` change plusieurs fois (chargement, refetch…).
      announcementModalShown: false,
      announcementModal: null,
    };
  },
  computed: {
    authUser() {
      return this.$store.getters['auth/user'];
    },
    authStore() {
      return this.$store.getters['auth/store'];
    },
    canShowAnnouncementModal() {
      // Check if current date is in the announcement period (use time travel date if available)
      const now = getCurrentDate();
      const isInDateRange = now >= this.announcementStartDate && now <= this.announcementEndDate;

      // Check if user has seen announcement today
      const lastSeenAt = localStorage.getItem('last-announcement-seen-at');
      const lastSeenDate = lastSeenAt ? new Date(parseInt(lastSeenAt, 10)) : null;

      // Check if user has seen the announcement today (same day) - use time travel date
      const today = getCurrentDate();

      // Normalize both dates to start of day for comparison
      const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const lastSeenNormalized = lastSeenDate ? new Date(lastSeenDate.getFullYear(), lastSeenDate.getMonth(), lastSeenDate.getDate()) : null;

      const hasSeenToday = lastSeenNormalized && todayNormalized.getTime() === lastSeenNormalized.getTime();

      return (
        !this.$route.meta.noGlobalPrompts
        && !this.canShowStoreOnboarding
        && this.authUser
        && this.$store.getters['auth/isRoleOwner']
        // Show for all users (content will differ based on plan)
        && isInDateRange
        && !hasSeenToday
      );
    },
    canShowStoreOnboarding() {
      return (
        this.authUser
        && this.$store.getters['auth/isRoleOwner']
        && !this.$route.meta.noOnboarding
        && (
          (!!this.$route.query.questionnaire)
          || !this.authStore.onboarding_questions
        )
      );
    },
  },
  watch: {
    '$route.meta.noGlobalPrompts': function(suppressed) {
      if (suppressed && this.announcementModal) {
        this.announcementModal.close();
        this.announcementModal = null;
      }
    },
    // La popup dépend de l'utilisateur chargé. À la connexion, App.vue n'est pas
    // ré-instancié (navigation SPA), donc un contrôle unique dans created() ne se
    // redéclenchait qu'au rechargement complet de la page. On réagit désormais à
    // l'apparition de `authUser` : ça couvre le refresh (déjà connecté) ET la
    // connexion fraîche (null → utilisateur).
    authUser: {
      immediate: true,
      handler() {
        this.maybeShowAnnouncementModal();
      },
    },
  },
  created() {
    // Garantit le chargement de la session (et donc le renseignement d'`authUser`
    // qui déclenchera le watch ci-dessus).
    this.$store.dispatch('auth/ready')
      .catch((error) => this.$errorHandlers.axios(error));
  },
  methods: {
    maybeShowAnnouncementModal() {
      if (this.announcementModalShown || !this.canShowAnnouncementModal) {
        return;
      }
      this.announcementModalShown = true;
      this.announcementModal = this.$buefy.modal.open({
        parent: this,
        canCancel: true,
        component: AnnouncementModal,
        width: 640,
        hasModalCard: true,
        props: {
          hasPercentPlan: this.$store.getters['auth/hasPercentPlan'],
        },
        onCancel: () => this.rememberAnnouncementSeen(),
        events: {
          close: () => this.rememberAnnouncementSeen(),
        },
      });
    },
    rememberAnnouncementSeen() {
      const currentDate = getCurrentDate();
      const normalizedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
      localStorage.setItem('last-announcement-seen-at', normalizedDate.getTime());
    },
  },
};
</script>

<template>
  <div class="layout">
    <StagingBanner />
    <MFLayout v-if="$store.state.auth.ready">
      <template v-if="$store.state.errors.code">
        <Page404 />
      </template>
      <template v-else>
        <router-view />
      </template>
      <b-modal
        v-if="authUser"
        :active="canShowStoreOnboarding"
        :can-cancel="false"
        :full-screen="isMobileScreen"
        has-modal-card>
        <OnboardingQuestions :isMobileScreen="isMobileScreen" />
      </b-modal>
    </MFLayout>
    <AskOwnerPhone v-if="$store.state.auth.ready && !$route.meta.noGlobalPrompts" />
    <b-loading v-if="!$store.state.auth.ready" class="has-background-light" active />
    <teleport-target name="modals" multiple />
    <ResponsiveNoAccessMessage v-if="$store.state.auth.ready" />
    <Offline />
  </div>
</template>

<style lang="scss" scoped>
.mobile-message {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: $theme_color_black;
}
</style>
